import Vue from 'vue';
import _ from 'lodash';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import WidgetFrame from 'components/widgets/WidgetFrame/WidgetFrame.vue';
import icon from 'assets/img/dashboards/dash-pivot-table.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import { BfButton } from 'components/Butterfly';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import Table from './Table.vue';
import UpDown from 'components/widgets/UpDown/UpDown.vue';
import ExportModal from './ExportModal.vue';
import { comma } from 'src/utils/formatters';
const COLOR_PRESETS = {
    RED_GREEN: [
        'rgb(230, 124, 115, 1)',
        'rgb(243, 190, 185, 1)',
        'rgb(255, 255, 255, 1)',
        'rgb(171, 221, 197, 1)',
        'rgb(87, 187, 138, 1)',
    ],
    BLUE_ORANGE: [
        'rgb(254, 156, 82, 1)',
        'rgb(255, 206, 170, 1)',
        'rgb(221, 221, 221, 1)',
        'rgb(158, 203, 236, 1)',
        'rgb(60, 151, 218, 1)',
    ],
};
const DEFAULT_BIN_COLORS = [
    'rgb(6, 140, 204, 0.05)',
    'rgb(6, 140, 204, 0.25)',
    'rgb(6, 140, 204, 0.5)',
    'rgb(6, 140, 204, 0.75)',
    'rgb(6, 140, 204, 1)',
];
const DATE_BUCKET_OPTIONS = {
    'None': null,
    'Daily': 'D',
    'Weekly': 'WS',
    'Fortnightly': '2W',
    'Monthly': 'MS',
    'Quarterly': 'QS',
    'Yearly': 'AS',
};
const PivotTable = Vue.extend({
    components: {
        DownloadExportButton,
        WidgetFrame,
        BfButton,
        Dropdown,
        DropdownItem,
        PivotTable: Table,
        UpDown,
        ExportModal,
    },
    inject: {
        featureFlags: {
            from: 'featureFlags',
            default: () => ({}),
        }
    },
    props: {
        exportName: { type: String, required: false, default: '' },
        devMode: { type: Boolean, required: false, default: false },
        zoomToRoute: { type: Object, required: false, default: null },
        isZoomed: { type: Boolean, required: false, default: false },
        banner: { type: Object, default: () => null, required: false },
        baseQuery: { type: Array, required: true },
        masked: { type: Boolean, required: false, default: false },
        data: { type: Object, required: false, default: null },
        schema: { type: Array, required: true },
        config: { type: Object, required: false, default: null },
        status: { type: String, required: false, default: '' },
        savedQueries: { type: Array, required: false, default: () => [] },
        currentModel: { type: Object, required: true },
        concepts: { type: Array, required: false, default: () => [] },
        dashboardId: { type: Number, required: true },
        hasSentiment: { type: Boolean, required: false, default: false },
        hasNps: { type: Boolean, required: false, default: false },
        weekStart: { type: String, required: false, default: null },
        themeGroups: { type: Array, required: false, default: () => [] },
    },
    data() {
        return {
            icon,
            errorIcon,
            hasErrored: false,
            width: 0,
            height: 0,
            stagedHideTotals: false,
            stagedRowFields: [],
            stagedColFields: [],
            stagedValueField: 'Frequency',
            stagedValueMethod: 'COUNT',
            stagedColorSetMethod: 'single',
            stagedColorPreset: 'RED_GREEN',
            stagedBinColors: DEFAULT_BIN_COLORS,
            stagedBucketDateOffset: 'None',
            table: null,
            COLOR_PRESETS,
            renderVisible: true,
            noneSelectedLabel: 'Select...',
            DATE_BUCKET_OPTIONS,
        };
    },
    computed: {
        switchTitle() {
            return this.stagedValueMethod === 'PERCENT OF ROW' ?
                "Total Counts are currently not supported for 'Percent of Row'" :
                "Toggle the total counts in the table";
        },
        colLabel() {
            return this.table.colFields.map(this.getVectorLabel).join(', ');
        },
        rowLabel() {
            return this.table.rowFields.map(this.getVectorLabel).join(', ');
        },
        // The first date field selected will be used to bucket the data,
        // as we can only specify one date field to bucket by per request.
        bucketDateField() {
            const stagedFields = [...this.table.rowFields, ...this.table.colFields];
            const dateField = stagedFields.find((f) => this.dateFields.includes(f.label));
            return dateField?.label;
        },
        stagedBucketDateField() {
            const stagedFields = [...this.stagedRowFields, ...this.stagedColFields];
            const dateField = stagedFields.find((f) => this.dateFields.includes(f));
            return dateField;
        },
        onscroll() {
            return _.throttle(() => {
                this.renderVisible &&
                    this.$refs.pivot.calculateVisible();
            }, 20);
        },
        hasUnappliedSettings() {
            if (!this.table)
                return false;
            return (this.stagedBucketDateOffset !== this.table.bucketDateOffset ||
                this.stagedValueField !== this.table.valueField ||
                this.stagedValueMethod !== this.table.valueMethod ||
                this.stagedColorSetMethod !== this.table.colorSetMethod ||
                this.stagedColorPreset !== this.table.colorPreset ||
                this.stagedHideTotals !== this.table.hideTotals ||
                JSON.stringify(this.stagedBinColors) !== JSON.stringify(this.table.binColors) ||
                JSON.stringify(this.stagedRowFields.filter((f) => f !== this.noneSelectedLabel)) !== JSON.stringify(this.rowFields) ||
                JSON.stringify(this.stagedColFields.filter((f) => f !== this.noneSelectedLabel)) !== JSON.stringify(this.colFields));
        },
        isLoading() {
            return this.status === 'fetching';
        },
        paletteDisabled() {
            return this.stagedValueMethod === 'PERCENT OF ROW';
        },
        displayPercentOfRowWarning() {
            return this.stagedValueMethod === 'PERCENT OF ROW' && (this.colFields.includes('Themes') || this.colFields.includes('Top Concepts') || this.colFields.includes('Theme Groups'));
        },
        dataRows() {
            let payload = this.data?.payload ?? [];
            // If there are no nonzero `(No Value)` counts in the data, it is better
            // to not show them at all. `(No Value)` is a special segment value
            // that we inject to represent null values in the data. These are
            // included in the backend during aggregation and pivot calculations.
            payload = payload.filter((item) => {
                // Is this a (No Value) row or column? We have to check
                // all the row fields and all the column fields to see if
                // any of them is (No Value).
                let isNoValue = this.stagedRowFields.filter((field) => item[field] === '(No Value)').length > 0
                    || this.stagedColFields.filter((field) => item[field] === '(No Value)').length > 0;
                if (isNoValue) {
                    // It is a (No Value) row or column. Is the value zero?
                    // If so, we don't want to show it.
                    if (item.hasOwnProperty('value')) {
                        return item['value'] !== 0 && item['value'] !== null;
                    }
                    else if (item.hasOwnProperty('frequency_cov')) {
                        return item['frequency_cov'] !== 0;
                    }
                    else {
                        return true;
                    }
                }
                else {
                    return true;
                }
            });
            return payload;
        },
        hasValidData() {
            return this.data && (this.rowFields.length > 0 || this.colFields.length > 0);
        },
        fieldOptions() {
            const getSegmentCount = (field) => {
                const data = this.currentModel.metadata_info[field];
                return data?.values ?? 0;
            };
            const options = this.schema
                .filter((row) => row.typename !== 'TEXT')
                .map((row) => row.name)
                .map((name) => ({
                segments: getSegmentCount(name),
                name,
            }));
            if (this.savedQueries.length > 0) {
                options.push({
                    segments: this.savedQueries.length,
                    name: 'Themes',
                });
            }
            if (this.featureFlags?.theme_groups && this.themeGroups.length > 0) {
                options.push({
                    segments: this.themeGroups.length,
                    name: 'Theme Groups',
                });
            }
            if (this.concepts.length > 0) {
                options.push({
                    segments: this.concepts.length,
                    name: 'Top Concepts',
                });
            }
            if (this.hasSentiment) {
                options.push({
                    segments: 4,
                    name: 'Sentiment',
                });
            }
            // Disable options that are already selected
            options.forEach((option) => {
                let disabled = this.stagedRowFields.includes(option.name) ||
                    this.stagedColFields.includes(option.name);
                // If Themes or Top Concepts is in fields, disable both from being selected.
                // See shortcut story [sc-44537] for details.
                if (['Themes', 'Theme Groups', 'Top Concepts'].includes(option.name)) {
                    disabled =
                        this.stagedColFields.concat(this.stagedRowFields).includes('Themes') ||
                            this.stagedColFields.concat(this.stagedRowFields).includes('Top Concepts') ||
                            this.stagedColFields.concat(this.stagedRowFields).includes('Theme Groups');
                }
                option.disabled = disabled;
            });
            // Sort alphabetically
            options.sort((a, b) => a.name.localeCompare(b.name));
            return options;
        },
        rowFields() {
            return (this.table?.rowFields ?? [])
                .map((r) => r.label)
                .filter((r) => r !== this.noneSelectedLabel);
        },
        colFields() {
            return (this.table?.colFields ?? [])
                .map((r) => r.label)
                .filter((r) => r !== this.noneSelectedLabel);
        },
        cssBinVars() {
            if (!this.table || this.table.colorSetMethod === 'none')
                return {};
            return {
                '--bg-color-0': this.table.binColors[0],
                '--bg-color-1': this.table.binColors[1],
                '--bg-color-2': this.table.binColors[2],
                '--bg-color-3': this.table.binColors[3],
                '--bg-color-4': this.table.binColors[4],
            };
        },
        valueFieldOptions() {
            const fieldOptions = this.schema
                .filter((row) => ['NUMBER'].includes(row.typename))
                .map((row) => row.name);
            fieldOptions.push('Frequency');
            if (this.hasSentiment) {
                fieldOptions.push('Sentiment');
            }
            if (this.hasNps) {
                fieldOptions.push('NPS', 'NPS Impact');
            }
            return fieldOptions;
        },
        valueMethodOptions() {
            if (this.NPSFields.includes(this.stagedValueField) ||
                this.stagedValueField === 'NPS Impact' ||
                this.stagedValueField === 'NPS') {
                return [];
            }
            if (this.stagedValueField === 'Frequency') {
                return ['COUNT', 'PERCENT OF DATA', 'PERCENT OF ROW'];
            }
            if (this.stagedValueField === 'Sentiment') {
                return ['Positive PERCENT', 'Negative PERCENT', 'Mixed PERCENT'];
            }
            return ['COUNT', 'SUM', 'MEAN', 'MEDIAN', 'MIN', 'MAX'];
        },
        NPSFields() {
            return this.schema
                .filter((row) => row.typename === 'NPS')
                .map((row) => row.name);
        },
        dateFields() {
            return this.schema
                .filter((row) => ['DATE', 'DATE_TIME'].includes(row.typename))
                .map((row) => row.name);
        },
        grandTotalLabel() {
            if (['COUNT', 'SUM'].includes(this.table.valueMethod)) {
                return 'Grand Total';
            }
            if (this.table.valueMethod === 'NPS') {
                return 'Mean of NPS';
            }
            else if (this.table.valueMethod === 'NPS Impact') {
                return 'Mean of NPS Impact';
            }
            const str = this.table.valueMethod.toLowerCase();
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        // Normally we map the value field to the `value` key,
        // but NPS is a special case
        valueKey() {
            if (this.table.valueField === 'NPS') {
                return 'NPS Category|nps__';
            }
            if (this.table.valueField === 'NPS Impact') {
                return 'NPS Category|npsi_rto__';
            }
            return 'value';
        },
    },
    watch: {
        stagedValueMethod: {
            handler(newVal) {
                if (newVal === 'PERCENT OF ROW') {
                    this.stagedHideTotals = true;
                    this.stagedColorSetMethod = 'none';
                }
            },
            immediate: true
        },
        config: {
            deep: true,
            handler(newVal, oldVal) {
                if (!_.isEqual(oldVal, newVal)) {
                    this.setOptionsFromConfig();
                    this.updateTableSettings(false);
                }
            },
        },
    },
    mounted() {
        this.setOptionsFromConfig();
        this.updateTableSettings(false);
    },
    methods: {
        async getExportEl() {
            const rowMap = this.$refs.pivot.rowMap;
            const colMap = this.$refs.pivot.colMap;
            const fields = rowMap.concat(colMap);
            const res = await this.$refs.exportModal.open(fields);
            return new Promise(async (resolve) => {
                setTimeout(async () => {
                    // We need to render the entire table
                    const renderSetting = this.renderVisible;
                    this.renderVisible = false;
                    // Wait for the DOM to update
                    await Vue.nextTick();
                    const el = document.querySelector('.table-wrapper')?.cloneNode(true);
                    const wrapper = document.createElement('div');
                    wrapper.style.position = 'absolute';
                    wrapper.style.top = '100%';
                    wrapper.style.left = '100%';
                    document.body.appendChild(wrapper);
                    wrapper.appendChild(el);
                    // Hide left-side number cells
                    el.querySelectorAll('.number-header').forEach((e) => e.style.display = 'none');
                    Array.from(el.querySelectorAll('td, th'))
                        .forEach((el) => {
                        const coords = el.dataset.coords?.split('|') ?? [];
                        coords.forEach((coord) => {
                            if (!coord)
                                return;
                            const [label, value] = coord.split('=');
                            const show = res[label]?.find((v) => v == value);
                            if (show == undefined) {
                                // Take cell out of table flow
                                el.style.position = 'absolute';
                                el.style.top = '-100%';
                                el.style.left = '-100%';
                            }
                        });
                    });
                    resolve(el);
                    await Vue.nextTick();
                    this.renderVisible = renderSetting;
                    await Vue.nextTick();
                    el.remove();
                    this.$refs.exportModal.close();
                }, 1000);
            });
        },
        getVectorLabel(v) {
            let suffix = '';
            if (this.bucketDateField === v.label) {
                const offset = this.table.bucketDateOffset;
                const offsetLabel = DATE_BUCKET_OPTIONS[offset];
                if (offsetLabel) {
                    suffix = `\u00a0(${DATE_BUCKET_OPTIONS[offset]})`;
                }
            }
            return `${v.label.replace(' ', '\u00a0')}${suffix}`;
        },
        updateConfig() {
            const updated = Object.assign({}, this.config, {
                options: {
                    rowFields: this.rowFields,
                    colFields: this.colFields,
                    binColors: this.table.binColors,
                    colorSetMethod: this.table.colorSetMethod,
                    colorPreset: this.table.colorPreset,
                    valueField: this.table.valueField,
                    valueMethod: this.table.valueMethod,
                    bucketDateOffset: this.table.bucketDateOffset,
                    hideTotals: this.table.hideTotals,
                }
            });
            this.$emit('config-changed', updated);
        },
        setOptionsFromConfig() {
            this.stagedRowFields = this.config?.options?.rowFields ?? [];
            this.stagedColFields = this.config?.options?.colFields ?? [];
            this.stagedColorSetMethod = this.config?.options?.colorSetMethod ?? 'single';
            this.stagedBinColors = this.config?.options?.binColors ?? DEFAULT_BIN_COLORS;
            this.stagedColorPreset = this.config?.options?.colorPreset ?? 'RED_GREEN';
            this.stagedValueField = this.config?.options.valueField ?? 'Frequency';
            this.stagedValueMethod = this.config?.options.valueMethod ?? 'COUNT';
            this.stagedValueMethod = this.config?.options.valueMethod ?? 'COUNT';
            this.stagedBucketDateOffset = this.config?.options.bucketDateOffset ?? 'None';
            this.stagedHideTotals = this.config?.options.hideTotals ?? false;
            if (!this.stagedRowFields?.length) {
                this.stagedRowFields = [this.noneSelectedLabel];
            }
            if (!this.stagedColFields?.length) {
                this.stagedColFields = [this.noneSelectedLabel];
            }
        },
        median(values) {
            values.sort((a, b) => a - b);
            const half = Math.floor(values.length / 2);
            if (values.length % 2)
                return values[half];
            return (values[half - 1] + values[half]) / 2;
        },
        updateTableSettings(updateConfig = true) {
            if (this.NPSFields.includes(this.stagedValueField) ||
                this.stagedValueField === 'NPS Impact') {
                this.stagedValueMethod = 'NPS';
            }
            const getSummaryLabel = (method) => {
                if (method === 'NPS')
                    return 'Mean NPS';
                if (method === 'COUNT')
                    return 'Total';
                if (method === 'SUM')
                    return 'Total';
                if (method === 'MEAN')
                    return 'Mean';
                if (method === 'MEDIAN')
                    return 'Median';
                if (method === 'MIN')
                    return 'Min';
                if (method === 'MAX')
                    return 'Max';
            };
            const rowFields = this.stagedRowFields
                .filter((f) => f !== this.noneSelectedLabel)
                .map(this.segmentGetter);
            const colFields = this.stagedColFields
                .filter((f) => f !== this.noneSelectedLabel)
                .map(this.segmentGetter);
            this.table = {
                bucketDateOffset: this.stagedBucketDateOffset,
                colorSetMethod: this.stagedColorSetMethod,
                colorPreset: this.stagedColorPreset,
                binColors: this.stagedBinColors.slice(),
                summaryLabel: getSummaryLabel(this.stagedValueMethod),
                valueField: this.stagedValueField,
                valueMethod: this.stagedValueMethod,
                reducer: this.cellReducer,
                hideTotals: this.stagedHideTotals,
                rowFields,
                colFields,
            };
            updateConfig && this.updateConfig();
            this.isZoomed && this.fetchData();
            updateConfig &&
                this.$analytics.track.pivotTable.updateSettings(this.table.colorSetMethod, `${this.table.valueField}, ${this.table.valueMethod}`, this.table.rowFields.map((f) => f.name).join(', '), this.table.colFields.map((f) => f.name).join(', '));
        },
        removeField(arrName, index) {
            this[arrName] =
                this[arrName].filter((_, i) => i !== index);
            if (!this[arrName].length) {
                this[arrName] = [this.noneSelectedLabel];
            }
        },
        updateField(arrName, index, value) {
            this[arrName] =
                this[arrName].map((field, i) => {
                    return i === index ? value : field;
                });
        },
        updateValueField(field) {
            this.stagedValueField = field;
            if (!this.valueMethodOptions.includes(this.stagedValueMethod)) {
                this.stagedValueMethod = this.valueMethodOptions[0];
            }
        },
        changeBinsSingleColor(val) {
            const nums = val.match(/\d+/g);
            if (nums)
                this.stagedBinColors = [
                    `rgb(${nums[0]}, ${nums[1]}, ${nums[2]}, 0.05)`,
                    `rgb(${nums[0]}, ${nums[1]}, ${nums[2]}, 0.25)`,
                    `rgb(${nums[0]}, ${nums[1]}, ${nums[2]}, 0.5)`,
                    `rgb(${nums[0]}, ${nums[1]}, ${nums[2]}, 0.75)`,
                    `rgb(${nums[0]}, ${nums[1]}, ${nums[2]}, 1)`,
                ];
        },
        changePresetBins(label) {
            if (label === 'RED_GREEN') {
                this.stagedBinColors = COLOR_PRESETS.RED_GREEN;
            }
            else if (label === 'BLUE_ORANGE') {
                this.stagedBinColors = COLOR_PRESETS.BLUE_ORANGE;
            }
        },
        setChartDimensions(width, height) {
            this.width = width;
            this.height = height;
        },
        async fetchData(force = false) {
            this.hasErrored = false;
            let fields = this.rowFields.concat(this.colFields);
            fields = Array(...new Set(fields));
            let themes = [];
            let cutExclude = [];
            if (fields.includes('Themes')) {
                cutExclude = [{ column: 'group__', value: 'overall__' }];
                fields = fields.filter((f) => f !== 'Themes');
                themes.push(...this.savedQueries.map((q) => ({
                    value: q.query_value,
                    name: `theme_${q.name}`,
                })));
            }
            if (fields.includes('Theme Groups')) {
                cutExclude = [{ column: 'group__', value: 'overall__' }];
                fields = fields.filter((f) => f !== 'Theme Groups');
                themes.push(...this.themeGroups.map((q) => ({
                    value: q.query_value,
                    name: q.name,
                })));
            }
            if (fields.includes('Top Concepts')) {
                cutExclude = [{ column: 'group__', value: 'overall__' }];
                fields = fields.filter((f) => f !== 'Top Concepts');
                themes.push(...this.concepts.map((c) => ({
                    value: c.query_value,
                    name: `concept_${c.name}`,
                })));
            }
            const blocks = [];
            if (this.table.valueField === 'Frequency' &&
                this.table.valueMethod === 'PERCENT OF DATA') {
                blocks.push({
                    'aggfuncs': [
                        {
                            'new_column': 'frequency_cov',
                            'aggfunc': 'count',
                            'src_column': 'document_id'
                        }
                    ],
                    'metric_calculator': 'coverage',
                    'cutfuncs': [
                        {
                            'new_column': 'value_binned',
                            'src_column': 'frequency_cov',
                            'bins': '5',
                            'exclude': cutExclude,
                        }
                    ],
                });
            }
            if (this.NPSFields.includes(this.table.valueField) ||
                this.table.valueField === 'NPS Impact' ||
                this.stagedValueField === 'NPS') {
                blocks.push({
                    'aggfuncs': [
                        {
                            'src_column': 'document_id',
                            'new_column': 'frequency',
                            'aggfunc': 'count',
                        },
                    ],
                    'cutfuncs': [
                        {
                            'new_column': 'value_binned',
                            'src_column': this.valueKey,
                            'exclude': cutExclude,
                            'bins': '5',
                        }
                    ],
                    'metric_calculator': 'nps',
                    'pivot_field': 'NPS Category',
                });
            }
            if (this.table.valueField === 'Sentiment' ||
                fields.includes('Sentiment')) {
                fields = fields.filter((f) => f !== 'Sentiment');
                blocks.push({
                    'aggfuncs': [
                        {
                            'new_column': 'frequency',
                            'src_column': 'document_id',
                            'aggfunc': 'count',
                        }
                    ],
                    'pivot_field': 'sentiment__',
                    'metric_calculator': 'sentiment',
                });
            }
            // Default block
            if (blocks.length === 0) {
                let aggfunc = this.table.valueMethod.toLowerCase();
                if (aggfunc === 'percent of row') {
                    aggfunc = 'count';
                }
                blocks.push({
                    'aggfuncs': [
                        {
                            'new_column': 'value',
                            'aggfunc': aggfunc,
                            'src_column': ['Frequency'].includes(this.table.valueField)
                                ? 'document_id'
                                : this.table.valueField,
                        },
                    ],
                    'cutfuncs': [
                        {
                            'new_column': 'value_binned',
                            'src_column': 'value',
                            'exclude': cutExclude,
                            'bins': '5',
                        }
                    ],
                });
            }
            let dateAgg = {};
            if (this.bucketDateField && this.table.bucketDateOffset !== 'None') {
                fields = fields.filter((f) => f !== this.bucketDateField);
                const bucketDateOffset = this.table.bucketDateOffset;
                dateAgg = {
                    'date_fieldname': this.bucketDateField,
                    'date_aggregation_offset': DATE_BUCKET_OPTIONS[bucketDateOffset],
                    'week_start': this.weekStart,
                };
            }
            try {
                const requirements = {
                    'blocks': blocks,
                    'agg_fields': fields,
                    'queries': themes,
                    'filters': [],
                    ...dateAgg,
                };
                this.$emit('requires', 'pivot-table', requirements, force, true // ensure this request has filters applied
                );
            }
            catch (e) {
                console.error(e);
                this.hasErrored = true;
            }
        },
        // Determines which group(s) (row or column) a datum belongs to
        segmentGetter(field) {
            return {
                label: field,
                getter: (item) => {
                    if (field === 'Sentiment') {
                        return ['Positive', 'Negative', 'Neutral', 'Mixed'];
                    }
                    const isTheme = item['group__'].startsWith('theme_');
                    if (field === 'Themes' && isTheme) {
                        return [item['group__'].replace(/^theme_/, '')];
                    }
                    const isGroup = item['group__'].startsWith('group_');
                    if (field === 'Theme Groups' && isGroup) {
                        return [item['group__'].replace(/^group_/, '')];
                    }
                    const isConcept = item['group__'].startsWith('concept_');
                    if (field === 'Top Concepts' && isConcept) {
                        return [item['group__'].replace(/^concept_/, '')];
                    }
                    return [item[field]];
                },
            };
        },
        cellReducer(data, keys, formatValue = true) {
            const getValue = () => {
                if (data.length === 0)
                    return '';
                const values = [];
                // Get the coverage &, except in the case of sentiment, which
                // due to the way the pivot endpoint's metric calculation works
                // has its segments' % figures  all contained within a single
                // datapoint (in sentiment__|<segment>%__ attributes).
                if (this.table.valueField === 'Frequency' &&
                    this.table.valueMethod === 'PERCENT OF DATA' &&
                    !keys.find((k) => k.includes('Sentiment'))) {
                    const vals = data.map((i) => i.coverage_doc_rto__ ?? 0);
                    values.push(_.sum(vals) * 100);
                }
                // We display % of positive sentiment when sentiment % is
                // selected as the value field & calculation.
                if (this.table.valueField === 'Sentiment' &&
                    this.table.valueMethod === 'Positive PERCENT') {
                    const vals = data.map((i) => i['sentiment__|positive%__'] ?? 0);
                    values.push(_.mean(vals));
                }
                else if (this.table.valueField === 'Sentiment' &&
                    this.table.valueMethod === 'Negative PERCENT') {
                    const vals = data.map((i) => i['sentiment__|negative%__'] ?? 0);
                    values.push(_.mean(vals));
                }
                else if (this.table.valueField === 'Sentiment' &&
                    this.table.valueMethod === 'Mixed PERCENT') {
                    const vals = data.map((i) => i['sentiment__|mixed%__'] ?? 0);
                    values.push(_.mean(vals));
                }
                const getForKey = (key) => {
                    values.push(...data.map((i) => i[key] ?? 0));
                };
                if (this.table.valueField === 'Frequency') {
                    const suffix = this.table.valueMethod === 'PERCENT OF DATA'
                        ? '%__'
                        : '';
                    // For GrandTotal cells relating to sentiment, we have to gather
                    // the data from several attributes on the same datapoint. Normally
                    // we'd expect a datapoint for each cell in the row/col to exist.
                    // Get sentiment values for grandTotal if:
                    // - Sentiment is part of the submitted fields
                    // - The value method is 'percent of Row' and Sentiment is
                    // part of the columns
                    keys.forEach((k) => {
                        if (k.includes('GrandTotal')) {
                            const fields = k.split(':')[1].split('|');
                            if (fields.includes('Sentiment') ||
                                (this.table.valueMethod === 'PERCENT OF ROW' && this.colFields.includes('Sentiment'))) {
                                getForKey(`sentiment__|positive${suffix}`);
                                getForKey(`sentiment__|negative${suffix}`);
                                getForKey(`sentiment__|neutral${suffix}`);
                                getForKey(`sentiment__|mixed${suffix}`);
                            }
                        }
                    });
                    // Get individual sentiment values
                    if (keys.includes('Sentiment:Positive')) {
                        getForKey(`sentiment__|positive${suffix}`);
                    }
                    if (keys.includes('Sentiment:Negative')) {
                        getForKey(`sentiment__|negative${suffix}`);
                    }
                    if (keys.includes('Sentiment:Neutral')) {
                        getForKey(`sentiment__|neutral${suffix}`);
                    }
                    if (keys.includes('Sentiment:Mixed')) {
                        getForKey(`sentiment__|mixed${suffix}`);
                    }
                }
                // Gather data for all other value fields
                getForKey(this.valueKey);
                // Aggregate values
                if (this.table.valueMethod === 'MEAN') {
                    return _.sum(values) / data.length;
                }
                if (this.table.valueMethod === 'MEDIAN') {
                    return this.median(values);
                }
                if (this.table.valueMethod === 'MIN') {
                    return _.min(values);
                }
                if (this.table.valueMethod === 'MAX') {
                    return _.max(values);
                }
                if (this.table.valueMethod === 'NPS') {
                    return _.sum(values) / data.length;
                }
                // COUNT, SUM
                return _.sum(values);
            };
            // Round to 2 decimal places
            let val = Math.round(getValue() * 100) / 100;
            return formatValue ? comma(val) : val;
        },
        methodLabel(method) {
            if (method === 'PERCENT OF DATA')
                return 'PERCENT %';
            return method;
        },
        getCsvData() {
            this.$analytics.track.pivotTable.exportCSV();
            return this.$refs.pivot.toCSV();
        },
        trackSort(type, vector) {
            this.$analytics.track.pivotTable.sortClick(`${type}: ${JSON.stringify(vector.item)}`, vector.direction);
        },
        // error panel items
        refresh() {
            window.location.reload();
        },
        reload() {
            this.fetchData(true);
        },
        contact() {
            try {
                window.Intercom('show');
            }
            catch (e) {
                console.warn('intercom show failed');
            }
        },
        makePptSlide(pptx) {
            // Pivot Table hasn't been rendered (e.g not zoomed)
            if (!this.$refs.pivot)
                return;
            const slide = pptx.addSlide();
            const rows = this.$refs.pivot.generatePptTable({
                fill: 'F7F9FA',
                color: '333333',
                align: 'center',
                bold: true,
            }, {});
            slide.addTable(rows, {
                x: 0.5,
                y: 0.5,
                border: {
                    color: 'E7E8E8',
                },
                colW: [2, 0.5],
            });
        },
        setMaxHeight(open, e) {
            if (open) {
                const el = e.target;
                const wrapperEl = el.closest('.dropdown');
                const widgetEl = el.closest('.pivot-table');
                const dropdownEl = wrapperEl?.querySelector('.dropdown-menu');
                const widgetBottom = widgetEl.getBoundingClientRect().bottom;
                const dropdownTop = dropdownEl.getBoundingClientRect().top;
                const maxHeight = widgetBottom - dropdownTop - 20;
                dropdownEl.style.maxHeight = `${maxHeight}px`;
            }
        },
    },
});
export default PivotTable;
